<template>
  <div
    :id="containerId"
    ref="container"
    class="autocomplete-container"
    :class="containerClasses"
  >
    <form @submit.prevent>
      <slot name="label-top" />
      <input
        :id="fieldId"
        ref="input"
        v-model="terms"
        v-focus.lazy="inputFocused"
        type="text"
        autocomplete="street-address"
        :class="fieldClasses"
        :placeholder="placeholder"
        :name="fieldName"
        @input="search()"
        @focus="handleFocus()"
        @keyup.enter="enter()"
        @keydown.down="down()"
        @keydown.up="up()"
        @keydown.esc="emptyResultsList()"
        @blur="inputFocused = false"
      />
      <slot name="label-bottom" />
      <ul
        v-if="results && results.length > 0"
        :id="containerId + '_' + 'results'"
        ref="resultsList"
        class="address-selector-suggestions"
        :class="listClasses"
        :style="resultsListStyle"
      >
        <li
          v-for="(result, index) of results"
          :id="'result_' + index"
          :key="index"
          :ref="'result_' + index"
          class="address-selector-suggestion"
          :class="computedListItemClasses(index)"
          @click.prevent="select(result)"
          @enter.prevent="select(result)"
        >
          {{ result.titel || result.tekst }}
        </li>
      </ul>
    </form>
  </div>
</template>
<script>
'use strict'
import { AddressSelectorService } from '../services/address-selector.service'
import { mixin } from '../directives/focus.directive'

export default {
  name: 'AddressSelector',
  mixins: [mixin],
  props: {
    // optional placeholder
    placeholder: {
      required: false,
      type: String,
      default: ''
    },
    // better targetting
    containerId: {
      required: true,
      type: String
    },
    containerClasses: {
      type: [String, Object],
      default: ''
    },
    // for improved accessibility when used with label
    fieldId: {
      required: true,
      type: String
    },
    activeFieldClasses: {
      type: [String, Object],
      default: 'active'
    },
    fieldClasses: {
      type: [String, Object],
      default: ''
    },
    // for use with form validation
    fieldName: {
      type: String,
      default: ''
    },
    listClasses: {
      type: [String, Object],
      default: ''
    },
    listItemClasses: {
      type: [String, Object],
      default: ''
    },
    // describe the field (optional)
    label: {
      required: false,
      type: String,
      default: ''
    },
    // determines the initial value of the field
    val: {
      required: false,
      type: String,
      default: ''
    },
    addressId: {
      required: false,
      type: String,
      default: ''
    },
    token: {
      required: false,
      type: String,
      default: ''
    },
    options: {
      type: Object,
      required: false,
      default: () => {
        return {}
      }
    },
    showMax: {
      type: Number,
      required: false,
      default: 5
    },
    resultsListStyle: {
      type: Object,
      default: () => {
        return {}
      }
    }
  },
  data () {
    return {
      results: [],
      selectedResult: null,
      selectedVej: null,
      suppressFocusSearch: false,
      terms: this.val || '',
      addressSelectorService: null,
      inputFocused: false,
      currentIndex: 0,
      initActions: true,
      listHeight: 0
    }
  },
  computed: {
    maxResults () {
      return this.showMax || this.results.length
    }
  },
  watch: {
    val (newVal) {
      this.terms = newVal
    },
    terms (newVal) {
      this.$emit('inputChanged', newVal)
    },
    listHeight (newVal) {
      this.$emit('listHeightUpdated', newVal)
    }
  },
  created () {
    const token = this.token || this.options.token || ''
    const mergedOptions = Object.assign({}, this.options, { token })

    this.addressSelectorService = new AddressSelectorService(mergedOptions)
    this.addressSelectorService.setRenderCallback(this.handleResults)
    this.addressSelectorService.setInitialRenderCallback(this.handleResults)
    this.addressSelectorService.setSelectCallback(this.handleSelected)

    if (this.addressId && this.initActions) {
      this.addressSelectorService.selectInitial(this.addressId)
    }
  },
  mounted () {
    document.addEventListener('click', this.handleClickOutside, true)
    document.addEventListener('focus', this.handleClickOutside, true)
  },
  beforeDestroy () {
    document.removeEventListener('click', this.handleClickOutside, true)
    document.removeEventListener('focus', this.handleClickOutside, true)
  },
  methods: {
    computedListItemClasses (index) {
      return Object.assign(
        { [this.activeFieldClasses]: this.isActive(index) },
        this.listItemClasses
      )
    },
    search () {
      this.inputFocused = true
      this.currentIndex = 0

      if (!this.terms || this.terms.length < this.addressSelectorService.options.minLength) {
        this.$set(this, 'results', [])
        this.listHeight = 0
        this.selectedVej = null
        return
      }

      this.addressSelectorService.update(this.scopedSearchText())
    },
    // Once a street has been picked the input only holds "<vejnavn> <husnr>", so the
    // postcode is appended to the query instead of the input. That keeps the results
    // inside the street the user actually chose, without pushing the caret past the
    // city name the way the full titel does.
    scopedSearchText () {
      const vej = this.selectedVej

      if (!vej || !vej.postnr || !vej.vejnavn) {
        return this.terms
      }

      const terms = this.terms.trim()

      // the user has typed past the picked street - drop the scope
      if (!terms.toLowerCase().startsWith(vej.vejnavn.toLowerCase())) {
        this.selectedVej = null
        return this.terms
      }

      return terms.indexOf(vej.postnr) === -1 ? `${terms} ${vej.postnr}` : terms
    },
    // Clicking a suggestion blurs the input, so restoring focus fires the focus handler
    // again. Left alone that would start a second search that races the lookup for the
    // item just picked - and is why clicking used to behave differently from Enter,
    // which never loses focus in the first place.
    handleFocus () {
      if (this.suppressFocusSearch) {
        this.suppressFocusSearch = false
        this.inputFocused = true
        return
      }

      this.search()
    },
    focusInputAtEnd () {
      this.$nextTick(() => {
        const input = this.$refs.input
        if (!input) {
          return
        }
        this.suppressFocusSearch = true
        input.focus()
        if (typeof input.setSelectionRange === 'function') {
          input.setSelectionRange(input.value.length, input.value.length)
        }
      })
    },
    handleResults (response) {
      this.emptyResultsList()
      let results = []
      if (response.length) {
        for (let item of response) {
          results.push(item)
        }
      }
      let max = this.showMax ? this.showMax : results.length
      this.$set(this, 'results', results.slice(0, max))
      this.$nextTick(() => {
        let resultsList = document.getElementById(
          this.containerId + '_' + 'results'
        )
        if (resultsList) {
          this.listHeight = resultsList.getBoundingClientRect().height
        }
      })
      if (this.results.length === 1 && this.initActions) {
        this.select(this.results[0])
      }
    },
    handleSelected (item) {
      if (!item) {
        return
      }

      this.$emit('select', item)
      this.$set(this, 'selectedResult', item)
      this.selectedVej = null
      this.terms = item.titel || item.tekst || ''
      this.inputFocused = false
      this.currentIndex = 0
      this.emptyResultsList()
      this.initActions = false
    },
    select (item) {
      if (!item) {
        return
      }

      this.$set(this, 'selectedResult', item)

      // Only a street-level hit gets shortened to the street name, so the caret lands
      // right after it and the house number can be typed straight away, the way the old
      // DAWA autocomplete behaved. The API returns no `tekst` on these, so the
      // structured vejnavn/postnr fields are used instead.
      // Anything else - `adresse`, and `husnummer` for a house number that holds several
      // addresses - already carries a house number, so it keeps its full titel.
      if (item.type === 'navngivenvejpostnummer') {
        this.selectedVej = { vejnavn: item.vejnavn || '', postnr: item.postnr || '' }
        this.terms = item.vejnavn ? `${item.vejnavn} ` : (item.titel || item.tekst || '')
        this.focusInputAtEnd()
      } else {
        this.selectedVej = null
        this.terms = item.titel || item.tekst || ''
      }

      this.inputFocused = true
      this.currentIndex = 0
      this.emptyResultsList()

      this.addressSelectorService.select(item)
    },
    enter () {
      this.select(this.results[this.currentIndex])
    },
    // When up pressed while suggestions are open
    up () {
      if (this.currentIndex > 0) {
        this.currentIndex--
      }
      this.scrollToResult(this.currentIndex)
    },
    // When up pressed while suggestions are open
    down () {
      if (this.currentIndex < this.results.length - 1) {
        this.currentIndex++
      }
      this.scrollToResult(this.currentIndex)
    },
    scrollToResult (index) {
      let el = document.getElementById(`result_${index}`)
      if (el != null) {
        el.parentNode.scrollTop = el.offsetTop - 50
      }
    },
    // For highlighting element
    isActive (index) {
      return index === this.currentIndex
    },
    emptyResultsList () {
      this.listHeight = 0
      this.$set(this, 'results', [])
    },
    handleClickOutside (e) {
      const el = this.$refs.container
      if (
        (e.target !== this.$refs.input &&
          e.target !== this.$refs.resultsList) ||
        !el.contains(e.target)
      ) {
        // Removed to work in IE and still works in Chrome etc.
        // this.emptyResultsList()
      }
    }
  }
}
</script>
